import colors from 'styles/colors';
import { useMediaQuery } from 'react-responsive';
import { Icon, SemanticICONS } from 'semantic-ui-react';
import i18n from 'helpers/i18n';
import { Status as StatusIcon } from 'components/Reports/Reliability/View';
import { passesTwoOfFiveRule } from '../../utils';
import messages from './messages';

interface CommonProps {
  name: string;
  abbreviation: string;
}
export interface DimensionReliability extends CommonProps {
  score: number;
  right_answer_per_video: boolean[];
}

export interface DimensionPreparation extends CommonProps {
  reliable: boolean;
}

interface DimensionResultsProps {
  dimensions: DimensionReliability[];
}

interface DimensionResultsPreparationProps {
  dimensions: DimensionPreparation[];
}

function isReliability(
  dimension: DimensionReliability | DimensionPreparation
): dimension is DimensionReliability {
  return (
    (dimension as DimensionReliability).right_answer_per_video !== undefined
  );
}

export default function DimensionResults({
  dimensions
}: DimensionResultsProps | DimensionResultsPreparationProps) {
  const isTablet = useMediaQuery({ minWidth: 640 });

  function getBackgroundColor(right_answer_per_video: boolean[]) {
    return passesTwoOfFiveRule(right_answer_per_video)
      ? colors.reliability.passVideo
      : colors.reliability.failedAttempt;
  }

  function getIcon(right_answer_per_video: boolean[]): SemanticICONS {
    if (passesTwoOfFiveRule(right_answer_per_video)) {
      return 'check';
    }
    return 'close';
  }

  function getDimensionStatusLabel(right_answer_per_video: boolean[]) {
    return passesTwoOfFiveRule(right_answer_per_video)
      ? i18n.ft(messages.twoOfFiveRulePassed)
      : i18n.ft(messages.twoOfFiveRuleFailed);
  }

  function getColor(dimension: DimensionPreparation) {
    if (!isReliability(dimension)) {
      return dimension.reliable
        ? colors.reliability.passVideo
        : colors.reliability.failedAttempt;
    }
    return colors.reliability.failedAttempt;
  }

  function getName(dimension: DimensionPreparation) {
    if (dimension.reliable) {
      return 'check';
    }
    return 'close';
  }

  if (!dimensions) {
    return null;
  }

  return (
    <>
      {dimensions.map((dimension, idx: number) => (
        <tr key={idx} className="text-sm md:text-xl">
          <th
            scope="row"
            className="py-2 border border-white pl-2"
            style={{ background: colors.scaleSection }}
          >
            <div className="flex items-center">
              {isReliability(dimension) && (
                <>
                  <span className="sr-only">
                    {getDimensionStatusLabel(
                      dimension.right_answer_per_video || []
                    )}
                  </span>
                  <StatusIcon
                    aria-hidden="true"
                    iconColor={colors.white}
                    iconBackGroundColor={getBackgroundColor(
                      dimension.right_answer_per_video || []
                    )}
                    className="!flex !mt-auto !mb-auto mr-6 shrink-0"
                    size={isTablet ? '24px' : '15px'}
                  >
                    <Icon
                      aria-hidden="true"
                      name={getIcon(dimension.right_answer_per_video || [])}
                      size="tiny"
                      className="align-middle !m-auto"
                      style={{
                        fontSize: !isTablet ? '0.35em' : '0.5em',
                        position: 'relative',
                        margin: 'auto'
                      }}
                    />
                  </StatusIcon>
                </>
              )}
              <span className="text-sm md:text-base font-normal">
                <span className="hidden md:inline">{dimension.name}</span>
                <span className="inline md:hidden ml-6">
                  {dimension.abbreviation}
                </span>
              </span>
            </div>
          </th>
          {isReliability(dimension) && (
            <td
              className="text-center py-2 text-black border border-white"
              style={{ backgroundColor: colors.scaleSection }}
            >
              <span className="text-xs self-center sm:text-sm md:text-base">
                {dimension.score}%
              </span>
            </td>
          )}
          {isReliability(dimension) &&
            dimension.right_answer_per_video.map((result, vidIdx) => (
              <td
                key={vidIdx}
                className="text-center font-bold py-2 border"
                style={{ borderColor: colors.scaleSection }}
              >
                <span className="sr-only">
                  {result ? i18n.ft(messages.passed) : i18n.ft(messages.failed)}
                </span>
                <div
                  className="text-2xl text-center flex justify-center items-center h-full"
                  style={{
                    color: result
                      ? colors.reliability.passVideo
                      : colors.reliability.failedAttempt
                  }}
                  aria-hidden="true"
                >
                  <Icon
                    name={result ? 'check' : 'close'}
                    size={isTablet ? 'small' : 'tiny'}
                    className="align-middle"
                    style={{ fontSize: isTablet ? '0.85em' : '0.7em' }}
                  />
                </div>
              </td>
            ))}
          {!isReliability(dimension) && (
            <td
              className="text-center font-bold py-2 border"
              style={{ borderColor: colors.scaleSection }}
            >
              <span className="sr-only">
                {getColor(dimension) === colors.reliability.passVideo
                  ? i18n.ft(messages.reliable)
                  : i18n.ft(messages.notReliable)}
              </span>
              <div
                className="text-2xl text-center flex justify-center items-center h-full"
                style={{ color: getColor(dimension) }}
                aria-hidden="true"
              >
                <Icon
                  name={getName(dimension)}
                  size={isTablet ? 'small' : 'tiny'}
                  className="align-middle"
                  style={{ fontSize: isTablet ? '0.85em' : '0.7em' }}
                />
              </div>
            </td>
          )}
        </tr>
      ))}
    </>
  );
}
